<template>
    <div id="atlas-tra">
        <v-select v-if="datetime_list.length !== 0"
            v-model="current_datetime"
            :items="datetime_list"
            id="atlas_table_datetime_select"
            class="date_time__select__box"
            item-text="text"
            item-value="value"
            :label="`${this.current_data.label || '選擇時間區間'}`"
            hide-details
        />
        <AtlasTable
            :id="current_data.dir"
            :key="current_data.dir"
            upper_left_corner="預報時間"
            :data_source="current_data_text"
            :titles="titles"
            :sidebars="current_data.kind"
            :images="images"
            @open-image-dialog="(image) => $emit('open-image-dialog', image)"
        />
    </div>
</template>

<script>
    import * as dayjs from 'dayjs';
    import utc from 'dayjs/plugin/utc';
    import { atlas_newest_data_ajax } from '@/utils/atlas/atlas.js';
    import { get_datetime_obj } from '@/utils/common/custom_time.js'
    import { pipe } from '@/utils/common/funtional'
    import {
        atlas_root_dir, 
        alt_src, 
        white_src,
    } from "@/config/atlas_route_config.js";
    import AtlasTable from "@/components/atlas/_tool/atlas_table.vue";
    import DOMPurify from 'dompurify';
    
    export default {
        name: 'AtlasTimeSelectTable',

        components: {
            AtlasTable,
        },

        props: {
            current_data: {
                type: Object,
                required: true,
            },
        },

        computed: {
            titles() {
                const { title_rule } = this.current_data;
                const rule_map = {
                    'initial-real': this.init_real_titles,
                }
                // [[['init.T+', '(06-09)h']],[['05/15', '(Sun)', ' 06-09 Z']]]
                return title_rule ? rule_map[title_rule]() : [this.current_data.titles.map(item => [item])];
            },

            current_data_text () {
                return DOMPurify.sanitize(this.current_data.text)
            }

        },

        data() {
            return {
                newest_date : '',
                newest_hour: '',
                
                alt_src: alt_src,

                path_list: [],
                datetime_list: [],
                current_datetime: '',
                images: {}
            }
        },

        watch: {
            current_datetime: {
                handler() {
                    if (this.current_datetime) this.init_images()
                },
                immediate: true
            },
        },

        created() {
            dayjs.extend(utc);
            this.generate_time_select_bar();
        },

        methods: {
            init_real_titles() {
                const { titles } = this.current_data;
                const first_row = titles.map(name => ['init.T+',`(${name})h`]);

                const now_date = dayjs(this.current_datetime.split(',')[0]).hour(Number(this.current_datetime.split(',')[1]));

                let fix_date = dayjs(now_date).add( 6, 'h'); // init.T+ (06-09)h
                const EACH_TIME_INCREASE = 3
                const second_row = titles.map(name => {
                    const start_hour = fix_date.format('HH');
                    fix_date = dayjs(fix_date).add( EACH_TIME_INCREASE, 'h');
                    const end_hour = fix_date.format('HH');
                    return [`${fix_date.format('MM/DD')}`, `(${fix_date.format('ddd')})`, ` ${start_hour}-${end_hour} Z`]
                })
                return [first_row, second_row]
            },

            async generate_time_select_bar() {
                const latest_date = await this._get_latest_date()

                if(!latest_date) {
                    return
                }

                const datetime_list = this._generate_selected_time_list(latest_date);
                const is_current_datetime_need_init = !datetime_list
                    .map(({ value }) => value)
                    .includes(this.current_datetime)
                this.datetime_list = datetime_list;
                if (is_current_datetime_need_init) {
                    this.current_datetime = datetime_list[0].value;
                }
            },

            async _get_latest_date() {
                const { kind, dir, path_use_kind, custom_kind } = this.current_data;
                const kind_list = custom_kind || kind;
                const path_list = kind_list.map(item => {
                    return `${atlas_root_dir}${dir}${path_use_kind ?item : ''}`;
                });
                this.path_list = path_list;
                const latest_date = await this._get_latest_date_request(path_list)
                return latest_date
            },

            async _get_latest_date_request(path_list) {
                const responses = await atlas_newest_data_ajax(path_list)

                const _latest_date_reducer = (result, date) => {
                    const current_result_date = new Date(...result.split(/[-,]+/))
                    const be_compare_date = new Date(...date.split(/[-,]+/))
                    return current_result_date >= be_compare_date ? result : date
                }

                return responses
                    .filter(response => response)
                    .map(response => response.data)
                    .reduce(_latest_date_reducer)
            },

            _generate_selected_time_list(latest_date) {
                const { select_rule } = this.current_data;
                const rule_map = {
                    'UTC': 'YYYY年MM月DD日 (ddd) HH00 UTC'
                };
                const text_format_rule = select_rule ? rule_map[select_rule] : 'YYYY年MM月DD日HH時';
                const date = latest_date.split(',')[0];
                const hour = latest_date.split(',')[1];
                let now_date = dayjs(date).hour(hour);
                const EACH_TIME_DIFFERENCE = 6; // 下拉選單以 6小時為區間
                return [...Array(4)].map( (item, index) => {
                    if (index > 0) now_date = dayjs(now_date).add(-EACH_TIME_DIFFERENCE, 'h');
                    return {
                        text: dayjs(now_date).format(text_format_rule),
                        value: dayjs(now_date).format('YYYY-MM-DD,HH')
                    }
                })
            },

            init_images() {
                const images = this.path_list.reduce((accum, path, index) => {
                    const data_kind = this.current_data.kind[index];
                    return { 
                        ...accum, 
                        [data_kind]: this.import_images({
                            path, 
                            data_kind: data_kind
                        }) 
                    }
                }, {})
                this.images = images;
            },

            import_images(options) {
                const { data_kind, path } = options;
                const { titles, path_rule, prefix, type } = this.current_data;
                const latest_date = this.current_datetime;
                const [newest_date, newest_hour] = latest_date.split(',');
                const failed_img = alt_src; // alt_src 藍字；white_src 白字
                const rule_func = {
                    'pre_kind_title': (title) => {
                        return {
                            src: `${path}/${prefix}_${data_kind}_${title}.${type}`,
                            ...this._common_parameter_generator(failed_img)(title)
                        }
                    }, // MIC 使用
                    'pre_date_time_kind_title': (title) => {
                        return {
                            src: `${path}/${prefix}_${newest_date}_${newest_hour}_${data_kind}_${title}.${type}`,
                            ...this._common_parameter_generator(failed_img)(title)
                        }
                    },
                    'date_time-pre-title': (title) => {
                        const name = title.split('-')[1];
                        const no_minus_date = newest_date.replaceAll('-', '');
                        return {
                            src: `${path}/${no_minus_date}_${newest_hour}${prefix}${name}.${type}`,
                            ...this._common_parameter_generator(failed_img)(title)
                        }
                    },
                    'pre_title': (title) => {
                        return {
                            src: `${path}/${prefix}_${title}.${type}`,
                            ...this._common_parameter_generator(failed_img)(title)
                        }
                    }
                };

                const images = titles.map(title => {
                    const rule = path_rule || (data_kind === 'MIC' ? 'pre_kind_title' : 'pre_date_time_kind_title');
                    return rule_func[rule](title);
                });
                return images;
            },

            _generate_image_of_objects({ file_names, get_src, get_common_parameter}) {
                return file_names.map(file_name => {
                    return {
                        src: get_src(file_name),
                        ...get_common_parameter(file_name),
                    }
                })
            },

            _use_white_alt_src() {
                return this._common_parameter_generator(white_src)
            },

            _use_cwa_alt_src() {
                return this._common_parameter_generator(alt_src)
            },

            _common_parameter_generator(alt_image_src) {
                return (file_name) => {
                    return {
                        alt_image: alt_image_src,
                        failed: false,
                        header_content: file_name
                    }
                }
            }
        },
    }
</script>

<style lang="scss">
    .select-label-bold label.v-label.v-label--active.theme--light {
        color: #000;
        font-size: 22px;
        font-weight: bold;
    }
</style>